import React, { useState } from 'react';
import { 
  X, 
  Database, 
  Plus, 
  RotateCcw, 
  Download, 
  Upload, 
  Check,
  Trash2 
} from 'lucide-react';
import { Project } from '../types';
import { StorageService } from '../services/storage';

interface ProjectManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects?: Project[];
  campaigns?: Project[]; // kept for backward compatibility
  currentProjectId?: string;
  currentCampaignId?: string; // kept for backward compatibility
  onSelectProject?: (id: string) => void;
  onSelectCampaign?: (id: string) => void; // kept for backward compatibility
  onRefreshData: () => void;
}

export const ProjectManagerModal: React.FC<ProjectManagerModalProps> = ({
  isOpen,
  onClose,
  projects,
  campaigns,
  currentProjectId,
  currentCampaignId,
  onSelectProject,
  onSelectCampaign,
  onRefreshData,
}) => {
  const projectList = projects || campaigns || [];
  const activeProjectId = currentProjectId || currentCampaignId || 'kfs-apc-2026';
  const handleSelect = onSelectProject || onSelectCampaign || (() => {});
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectFacility, setNewProjectFacility] = useState('Keyera Fort Saskatchewan');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [newProjectTargetMoves, setNewProjectTargetMoves] = useState<number>(20);
  const [newProjectHoldDuration, setNewProjectHoldDuration] = useState<number>(60);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    const id = `camp-${Date.now()}`;
    const newProject: Project = {
      id,
      name: newProjectName.trim(),
      facility: newProjectFacility.trim(),
      description: newProjectDesc.trim(),
      createdAt: new Date().toISOString(),
      status: 'Active',
      targetMovesPerMv: newProjectTargetMoves,
      defaultHoldDuration: newProjectHoldDuration,
    };

    await StorageService.createProject(newProject);
    handleSelect(id);
    onRefreshData();
    setNewProjectName('');
    setNewProjectDesc('');
    setNewProjectTargetMoves(20);
    setNewProjectHoldDuration(60);
  };

  const handleDeleteProject = async (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete project "${project.name}"?\n\nThis will completely erase the project and all of its variables, step tests, and disturbances from the database. This action cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(project.id);
    try {
      await StorageService.deleteProject(project.id);
      onRefreshData();
    } catch (err: any) {
      alert(`Failed to delete project: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleResetToSeed = () => {
    if (window.confirm('Reset local database to initial KFS APC Project with all 36 MVs?')) {
      StorageService.resetToKfsSeedData();
      onRefreshData();
      onClose();
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const jsonStr = await StorageService.exportFullBackup();
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `APC_Step_Test_Backup_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(`Export failed: ${e.message}`);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      setImportStatus('Restoring backup to database...');
      try {
        const res = await StorageService.importFullBackup(content);
        if (res.success) {
          setImportStatus('Backup restored successfully!');
          if (res.restoredProjectId) {
            await handleSelect(res.restoredProjectId);
          }
          await onRefreshData();
          setTimeout(() => setImportStatus(null), 3000);
        } else {
          setImportStatus(res.error || 'Failed to import JSON backup file.');
        }
      } catch (err: any) {
        setImportStatus(`Restore error: ${err.message || 'Unknown error'}`);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/40 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-xl overflow-hidden my-4 sm:my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-purple-600" />
            <h2 className="font-semibold text-slate-900 text-base">
              Project & Database Management
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5 max-h-[82vh] overflow-y-auto">
          {/* Active Project Selector */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Select Active Project
            </label>
            <div className="space-y-2">
              {projectList.map((p) => (
                <div
                  key={p.id}
                  onClick={() => handleSelect(p.id)}
                  className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between gap-3 ${
                    p.id === activeProjectId
                      ? 'bg-purple-50/50 border-purple-500 ring-2 ring-purple-500/20 shadow-xs'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-900 truncate">{p.name}</span>
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono text-slate-500">
                        {p.facility}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{p.description}</div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {p.id === activeProjectId && (
                      <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                        Active
                      </span>
                    )}

                    <button
                      type="button"
                      disabled={deletingId === p.id}
                      onClick={(e) => handleDeleteProject(e, p)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                      title={`Delete project "${p.name}" and all related data`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Create New Project Form */}
          <form onSubmit={handleCreateProject} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5 text-purple-600" />
              <span>Create New Project</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Project Title</label>
                <input
                  type="text"
                  placeholder="e.g. KFS South Fractionation Re-ID"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Facility</label>
                <input
                  type="text"
                  placeholder="Keyera Fort Saskatchewan"
                  value={newProjectFacility}
                  onChange={(e) => setNewProjectFacility(e.target.value)}
                  className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
              <input
                type="text"
                placeholder="Scope and target units..."
                value={newProjectDesc}
                onChange={(e) => setNewProjectDesc(e.target.value)}
                className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Target Moves Per MV</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={newProjectTargetMoves}
                  onChange={(e) => setNewProjectTargetMoves(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none font-mono"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Default Hold Duration (min)</label>
                <input
                  type="number"
                  min="5"
                  max="480"
                  value={newProjectHoldDuration}
                  onChange={(e) => setNewProjectHoldDuration(Math.max(5, parseInt(e.target.value) || 5))}
                  className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none font-mono"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition-colors shadow-xs"
              >
                Add Project
              </button>
            </div>
          </form>

          {/* Backup, Restore, and Reset */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <div className="text-xs font-semibold text-slate-700">
              Database Backup & Seed Tools
            </div>

            {importStatus && (
              <div className="p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-lg flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                <span>{importStatus}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={handleDownloadBackup}
                className="flex items-center justify-center gap-1.5 p-2 rounded-lg bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 text-xs font-medium transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5 text-slate-500" />
                <span>Export JSON</span>
              </button>

              <label className="flex items-center justify-center gap-1.5 p-2 rounded-lg bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 text-xs font-medium cursor-pointer transition-colors shadow-xs">
                <Upload className="w-3.5 h-3.5 text-emerald-600" />
                <span>Restore JSON</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>

              <button
                type="button"
                onClick={handleResetToSeed}
                className="flex items-center justify-center gap-1.5 p-2 rounded-lg bg-white hover:bg-rose-50 text-rose-700 border border-slate-300 hover:border-rose-200 text-xs font-medium transition-colors shadow-xs"
                title="Reset to pre-seeded 36 KFS manipulated variables"
              >
                <RotateCcw className="w-3.5 h-3.5 text-rose-500" />
                <span>Reset Seed Data</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CampaignManagerModal = ProjectManagerModal;

